/**
 * The browser's view of the API, plus the two operations that stay in the browser.
 *
 * ## Why export happens here
 *
 * The editor renders a panel with `renderScene` in the browser. Export does the same, in the same
 * process, from the same `SceneDoc` — so **what the editor shows is what the file contains**, byte
 * for byte. Routing the export through the server would break that by construction: the parity check
 * measures a 0-luma geometry difference and a 5-luma text difference between Skia and a browser,
 * which is small but is not identical.
 *
 * The server can still render (the API's jobs do), and that path exists for clients with no browser:
 * the CLI and the MCP server. Both are correct; only one of them can claim byte-identity.
 */
import { zipSync, strToU8 } from 'fflate';
import { createBrowserEnvironment } from '@keymaker/renderer/browser';
import { renderSceneComplete, substituteTokens, validatePanel, rgbaStats, type RenderImage } from '@keymaker/renderer';
import {
  DEFAULT_FAMILIES,
  bindPanel,
  familyByKey,
  panelFileName,
  templateIdForPanel,
  tokenValues,
  type AssetRow,
  type DeviceFrame,
  FrameLibrary,
  type PanelRow,
  type ProjectBundle,
} from '@keymaker/schema';
import { resolveForFamily } from '@keymaker/templates';

export interface ApiError extends Error {
  status: number;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body instanceof Uint8Array || init.body instanceof ArrayBuffer
        ? { 'content-type': 'application/octet-stream' }
        : init.body !== undefined
          ? { 'content-type': 'application/json' }
          : {}),
      ...(init.headers ?? {}),
    },
  });
  if (!response.ok) {
    let message = `${response.status} ${response.statusText}`;
    try {
      const body = (await response.json()) as { message?: string; error?: string };
      message = body.message ?? body.error ?? message;
    } catch {
      // The body was not JSON; the status line is the best we have.
    }
    const error = new Error(message) as ApiError;
    error.status = response.status;
    throw error;
  }
  const type = response.headers.get('content-type') ?? '';
  return (type.includes('application/json') ? await response.json() : (await response.blob())) as T;
}

export interface ProjectSummary {
  id: string;
  name: string;
  template_id: string;
  families: string[];
  asset_count: number | string;
  panel_count: number | string;
}

/**
 * The wire shapes come from the schema package, not from a copy kept here.
 *
 * This file used to declare its own `ProjectBundle`, and when the panel model changed that copy did
 * not — so the browser kept reading a project-level `edits` that the API had stopped sending and
 * nothing failed to compile.
 */
/**
 * Local names the rest of this file already uses.
 *
 * These are aliases rather than a `from` re-export on purpose: a `from` re-export would not bring the
 * names into scope here, which is how the file ends up referring to a type it no longer has.
 */
export type AssetSummary = AssetRow;
export type PanelSummary = PanelRow;
export type { ProjectBundle };

export interface TemplateSummary {
  id: string;
  name: string;
  category: string;
  description?: string;
  tags: string[];
  tokens: string[];
}

export const api = {
  me: () => request<{ id: string; email: string; via: string }>('/v1/auth/me'),
  signup: (email: string, password: string) =>
    request<{ id: string; email: string }>('/v1/auth/signup', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  login: (email: string, password: string) =>
    request<{ id: string; email: string }>('/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }),

  templates: () => request<{ templates: TemplateSummary[] }>('/v1/templates'),
  families: () =>
    request<{ families: { key: string; label: string; canvas: { width: number; height: number }; device: string }[] }>(
      '/v1/families',
    ),

  projects: () => request<{ projects: ProjectSummary[] }>('/v1/projects'),
  createProject: (name: string, templateId: string, families: string[]) =>
    request<{ id: string }>('/v1/projects', {
      method: 'POST',
      body: JSON.stringify({ name, templateId, families }),
    }),
  project: (id: string) => request<{ project: ProjectBundle }>(`/v1/projects/${id}`),
  patchProject: (id: string, patch: Record<string, unknown>) =>
    request<{ ok: boolean }>(`/v1/projects/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteProject: (id: string) => request<{ ok: boolean }>(`/v1/projects/${id}`, { method: 'DELETE' }),

  uploadAsset: (projectId: string, key: string, bytes: Uint8Array, screen?: string) =>
    request<{ key: string; width: number; height: number; sha256: string }>(
      `/v1/projects/${projectId}/assets?key=${encodeURIComponent(key)}${screen ? `&screen=${encodeURIComponent(screen)}` : ''}`,
      // Wrapped in a Blob rather than passed as a bare `Uint8Array`: `BodyInit` in the DOM types does
      // not admit a typed array directly, and a Blob is what `fetch` wants for binary anyway.
      { method: 'POST', body: new Blob([bytes as unknown as BlobPart]), headers: { 'content-type': 'image/png' } },
    ),
  /**
   * Replace the whole panel list.
   *
   * Every per-panel field goes with it — template, edits, background — because a panel *is* its scene,
   * and writing only the position would leave the rest to be maintained by a second path.
   */
  setPanels: (projectId: string, panels: PanelSummary[]) =>
    request<{ ok: boolean }>(`/v1/projects/${projectId}/panels`, {
      method: 'PUT',
      body: JSON.stringify({ panels }),
    }),
};

// ------------------------------------------------------------------ frame library

let frameCache: DeviceFrame[] | null = null;

/**
 * The frame library, fetched from the API's static route and validated against the schema.
 *
 * Parsed rather than trusted: raw JSON gives `device: string`, and a bad library would otherwise
 * surface as a device that silently fails to draw.
 */
export async function frames(): Promise<DeviceFrame[]> {
  if (frameCache) return frameCache;
  const response = await fetch('/frames/library.json');
  if (!response.ok) throw new Error(`Could not load the frame library (${response.status}).`);
  frameCache = FrameLibrary.parse(await response.json()).frames;
  return frameCache;
}

/** Load every frame variant once, keyed by asset path, for the renderer's asset map. */
let frameImageCache: Map<string, RenderImage> | null = null;

export async function frameImages(env: ReturnType<typeof createBrowserEnvironment>): Promise<Map<string, RenderImage>> {
  if (frameImageCache) return frameImageCache;
  const map = new Map<string, RenderImage>();
  for (const frame of await frames()) {
    for (const variant of frame.variants) {
      map.set(variant.asset, await env.loadImage(`/frames/${variant.asset}`));
    }
  }
  frameImageCache = map;
  return map;
}

// ----------------------------------------------------------------------- rendering

export interface RenderPanelResult {
  position: number;
  screen: string;
  filename: string;
  png: Uint8Array;
  sha256: string;
  problems: string[];
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as unknown as ArrayBuffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export interface RenderSetOptions {
  project: ProjectBundle;
  family: string;
  locale: string;
  /** Extra asset images the project's panels reference, keyed by asset key. */
  captures: Map<string, RenderImage>;
  env?: ReturnType<typeof createBrowserEnvironment>;
  /** Runs after each panel, so a caller can show progress on a 500-image run. */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Render every panel of a project for one family, in the browser.
 *
 * The scene binding here is the same two substitutions the server does — the panel's caption and the
 * panel's capture — because a preview built differently from the export is worse than no preview.
 */
export async function renderSet(options: RenderSetOptions): Promise<RenderPanelResult[]> {
  const env = options.env ?? createBrowserEnvironment();
  const library = await frames();
  // The declared family, not a hand-made object: `validatePanel` checks the render against the same
  // descriptor the server uses, so the browser cannot quietly accept a size the API would reject.
  const family = familyByKey(DEFAULT_FAMILIES, options.family);
  if (!family) throw new Error(`Unknown family "${options.family}".`);

  const assets = new Map<string, RenderImage>([...(await frameImages(env)), ...options.captures]);
  // The same helpers the server renders with. The export and the server cannot disagree about what a
  // panel looks like, because there is only one implementation of that question.
  const values = tokenValues(options.project, options.locale);

  const results: RenderPanelResult[] = [];
  const panels = [...options.project.panels].sort((a, b) => a.position - b.position);

  for (const panel of panels) {
    // Each panel resolves its own template: panel 1 can be a headline over a phone and panel 4 a
    // centred quote with no device at all.
    const base = resolveForFamily(templateIdForPanel(options.project, panel.position), options.family);
    const scene = substituteTokens(bindPanel(base, options.project, panel.position), values);
    // `renderSceneComplete` runs the Tier 3 pre-pass and then composites. Without it a scene
    // containing a `kind: "3d"` device throws "Call prepare3dDevices()" — the tier was implemented in
    // the renderer and never reachable from the app, which is why the 3D rotation had no UI and no
    // effect. Scenes with no 3D layers take the same fast path as before.
    const rendered = await renderSceneComplete(scene, env, { frames: library, assets });
    const rgba = rendered.canvas.toRgba();
    const stats = rgbaStats(rgba, rendered.width, rendered.height);

    const problems = validatePanel(
      { scene, canvas: family.canvas, bounds: rendered.bounds, rgba },
      family,
    ).map((problem) => problem.message);
    if (stats.transparentPixels > 0) problems.push(`${stats.transparentPixels} pixels are not opaque.`);
    for (const warning of rendered.warnings) if (!warning.includes('{{')) problems.push(warning);

    const png = await rendered.canvas.toPng();
    // The pixels are in `png` now, and a browser surface that lives in the document is kept alive by it.
    // Ten panels of fifteen megabytes, held for nothing.
    rendered.canvas.release?.();
    results.push({
      position: panel.position,
      screen: panel.screen,
      filename: `${family.key}/iphone-${String(panel.position).padStart(2, '0')}-${panelFileName(panel)}.png`,
      png,
      sha256: await sha256Hex(png),
      problems,
    });
    options.onProgress?.(results.length, panels.length);
  }

  return results;
}

/**
 * Package a rendered set as a ZIP, with a manifest whose hashes match the bytes inside it.
 *
 * Built here rather than requested from the server so the download is the exact set the user just
 * saw. The manifest shape is the one the API produces and the app factory's production run used, so
 * a set from either path is interchangeable.
 */
export function zipSet(
  project: ProjectBundle,
  family: string,
  locale: string,
  panels: RenderPanelResult[],
  canvas: { width: number; height: number },
): { zip: Uint8Array; manifest: Record<string, unknown> } {
  const manifest = {
    version: 1,
    language: locale,
    variations: 1,
    screenshots_per_device_set: Math.max(panels.length, 1),
    generatedAt: new Date().toISOString(),
    screenshots: panels.map((panel) => ({
      variation: 'default',
      device: family.startsWith('ipad') ? 'ipad' : 'iphone',
      position: panel.position,
      screen: panel.screen,
      theme: 'light',
      title: panel.screen,
      file: panel.filename,
      width: canvas.width,
      height: canvas.height,
      sha256: panel.sha256,
      family,
      locale,
    })),
  };

  const files: Record<string, Uint8Array> = {};
  for (const panel of panels) files[panel.filename] = panel.png;
  files['manifest.json'] = strToU8(`${JSON.stringify(manifest, null, 2)}\n`);
  files['README.txt'] = strToU8(
    [
      project.name,
      `Family: ${family}`,
      `Locale: ${locale}`,
      `Panels: ${panels.length}`,
      '',
      'Every PNG is truecolour with no alpha channel, which App Store Connect requires.',
      'manifest.json lists each file with its SHA-256.',
    ].join('\n'),
  );

  return { zip: zipSync(files, { level: 6 }), manifest };
}
